import { Fragment, memo } from "react";
import { Link } from "react-router-dom";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {
  QUEUE_JOB_STATUSES,
  type QueueJobStatus,
  type QueueRow,
} from "@/admin/entities/queues";
import { PagePaths } from "@/admin/shared/constants";
import { statusLabels } from "../model";
import { QueueMenu } from "./QueueMenu";

/**
 * Счётчик задач одного статуса: одновременно и цифра, и ссылка на список
 * задач очереди в этом статусе. Пустые статусы приглушены.
 */
function QueueCount({
  queueName,
  status,
  count,
}: {
  queueName: string;
  status: QueueJobStatus;
  count: number;
}) {
  return (
    <ButtonBase
      component={Link}
      to={`${PagePaths.queues}/${encodeURIComponent(queueName)}/${status}`}
      sx={{
        opacity: count > 0 ? 1 : 0.5,
        fontWeight: count > 0 ? "bold" : "normal",
        padding: 1,
        margin: -1,
        display: "block",
        // На узком экране счётчики раскладываются по три в ряд и ширину
        // задаёт сетка; фиксированные 140px там ломали бы карточку
        width: { xs: "100%", md: "auto" },
        minWidth: { xs: 0, md: 140 },
      }}
    >
      <Typography variant="h6" align="center">
        {count}
      </Typography>
      <Typography
        variant="body2"
        color={count > 0 ? "textSecondary" : "inherit"}
        align="center"
      >
        {statusLabels[status]}
      </Typography>
    </ButtonBase>
  );
}

export const QueueCard = memo(function QueueCard({
  queue,
}: {
  queue: QueueRow;
}) {
  return (
    <Card sx={{ padding: "8px 16px" }}>
      {/* Десктоп — строка, как в референсе: название, пять счётчиков через
          разделители, меню. Ниже md строка не помещается (пять счётчиков по
          140px + название), поэтому там название с меню занимают верхний
          ряд, а счётчики раскладываются по три в ряд. */}
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
      >
        <Grid
          size={{ xs: "grow", md: "auto" }}
          sx={{ marginRight: { md: "auto" }, minWidth: 0 }}
        >
          <Typography variant="h6" noWrap>
            {queue.name}
          </Typography>
        </Grid>

        {/* order держит меню последним на десктопе и поднимает его в первый
            ряд, к названию, на узком экране */}
        <Grid size="auto" sx={{ order: { xs: 1, md: 3 } }}>
          <QueueMenu queue={queue} />
        </Grid>

        {QUEUE_JOB_STATUSES.map((status, index) => (
          <Fragment key={status}>
            {index > 0 && (
              <Divider
                flexItem
                orientation="vertical"
                sx={{ order: 2, display: { xs: "none", md: "block" } }}
              />
            )}
            <Grid size={{ xs: 4, md: "auto" }} sx={{ order: 2 }}>
              <QueueCount
                queueName={queue.name}
                status={status}
                count={queue.jobCounts[status]}
              />
            </Grid>
          </Fragment>
        ))}
      </Grid>
    </Card>
  );
});
